import { useState } from 'react'
import { Brain, CheckCircle, AlertCircle, Loader2 } from 'lucide-react'
import { supabase } from '../lib/supabase'

const STEP_CONFIG = {
  scanning: { color: '#6b7280', icon: Loader2, spin: true  },
  memory:   { color: '#2563eb', icon: Loader2, spin: true  },
  saving:   { color: '#7c3aed', icon: Loader2, spin: true  },
  complete: { color: '#16a34a', icon: CheckCircle, spin: false },
  error:    { color: '#dc2626', icon: AlertCircle, spin: false },
}

function ProgressItem({ step, message, chapter, total }) {
  const cfg = STEP_CONFIG[step] || STEP_CONFIG.scanning
  const Icon = cfg.icon
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '5px 0' }}>
      <Icon
        size={13}
        style={{
          color: cfg.color,
          marginTop: 2,
          flexShrink: 0,
          animation: cfg.spin ? 'spin 1s linear infinite' : 'none',
        }}
      />
      <div style={{ flex: 1 }}>
        <span style={{ fontSize: '0.75rem', color: '#374151' }}>{message}</span>
        {chapter && total && (
          <div style={{ height: 3, borderRadius: 2, background: '#e5e7eb', marginTop: 4 }}>
            <div
              style={{
                height: '100%', borderRadius: 2, background: cfg.color,
                width: `${(chapter / total) * 100}%`, transition: 'width 0.4s',
              }}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default function ReindexPanel({ thesisId }) {
  const [state,    setState]    = useState('idle')   // idle | running | done | error
  const [events,   setEvents]   = useState([])

  async function run() {
    setState('running')
    setEvents([])

    const { data: { session } } = await supabase.auth.getSession()
    const token = session?.access_token
    if (!token) { setState('error'); return }

    const apiBase = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'

    try {
      const response = await fetch(`${apiBase}/thesis/${thesisId}/reindex`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      })

      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        const parts = buffer.split('\n\n')
        buffer = parts.pop()

        for (const part of parts) {
          const line = part.replace(/^data:\s*/, '').trim()
          if (!line) continue
          try {
            const evt = JSON.parse(line)
            setEvents(prev => [...prev, evt])
            if (evt.step === 'complete') setState('done')
            if (evt.step === 'error')    setState('error')
          } catch { /* ignore parse errors */ }
        }
      }
    } catch (err) {
      setEvents(prev => [...prev, { step: 'error', message: err.message }])
      setState('error')
    }
  }

  const isDone = state === 'done'
  const isRunning = state === 'running'

  return (
    <div>
      <button
        onClick={state === 'idle' || isDone || state === 'error' ? run : undefined}
        disabled={isRunning}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '0.5rem 0.875rem',
          background: isDone ? '#16a34a' : '#1e3a5f',
          color: isDone ? '#fff' : '#93c5fd',
          border: isDone ? 'none' : '1px solid rgba(147,197,253,0.3)',
          borderRadius: 8, fontSize: '0.8rem', fontWeight: 600,
          cursor: isRunning ? 'not-allowed' : 'pointer',
          opacity: isRunning ? 0.7 : 1,
        }}
      >
        {isRunning
          ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} />
          : <Brain size={13} />
        }
        {isRunning ? 'Rebuilding…' : isDone ? 'Done — Run Again' : 'Rebuild Thesis Intelligence'}
      </button>

      {events.length > 0 && (
        <div style={{ marginTop: 12 }}>
          {events.map((evt, i) => (
            <ProgressItem key={i} {...evt} />
          ))}
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}
